import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Order } from '../types/myaccount';
import api from '../services/api';
import MyAccountRecentTablePanel from './MyAccountRecentTablePanel';
import { formatMoneyWithCurrencyCode } from '../utils/formatMoneyWithCurrencyCode';

interface MyAccountOrdersPanelProps {
    maxOrders?: number;
    showHeader?: boolean;
    title?: string;
}

const MyAccountOrdersPanel: React.FC<MyAccountOrdersPanelProps> = ({
    maxOrders = 10,
    showHeader = true,
    title = 'Order History'
}) => {
    const [orders, setOrders] = useState<Order[] | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchOrders = async () => {
            try {
                setLoading(true);
                const dashboardData = await api.getAccountDashboardData();
                setOrders(Array.isArray(dashboardData.recent_orders) ? dashboardData.recent_orders.slice(0, maxOrders) : []);
            } catch (error) {
                console.error('Error fetching orders:', error);
                setOrders([]);
            } finally {
                setLoading(false);
            }
        };

        fetchOrders();
    }, [maxOrders]);

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const getErpId = (order: Order): string => {
        const erpId = order.erp_order_id;
        if (!erpId || erpId === 'no' || erpId === 'processing' || erpId === '0' || erpId === '') {
            return 'Processing';
        }
        return erpId;
    };

    const formatOrderTotal = (order: Order): string => formatMoneyWithCurrencyCode(
        order.total,
        order.currency,
        order.currency_display_mode ?? order.show_currency_code,
        order.currency_symbol,
    );

    return (
        <MyAccountRecentTablePanel
            title={title}
            showHeader={showHeader}
            loading={loading}
            data={orders}
            maxItems={maxOrders}
            skeletonColumns={3}
            containerClassName="order-history"
            tableStyle={{ tableLayout: 'fixed' }}
            emptyMessage="No orders found."
            columns={[
                { key: 'order', header: 'Order #', style: { width: '42%' } },
                { key: 'date', header: 'DATE', style: { width: '29%', whiteSpace: 'nowrap' } },
                { key: 'price', header: 'PRICE', style: { width: '29%', whiteSpace: 'nowrap' } },
            ]}
            getRowKey={(order) => order.id}
            renderRow={(order) => (
                <>
                    <td style={{ overflow: 'hidden' }}>
                        <Link
                            to={`/view-order/${order.id}`}
                            className="highlight-text weka-account-table-link"
                            style={{
                                display: 'inline-block',
                                width: '100%',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                verticalAlign: 'bottom',
                            }}
                            title={getErpId(order)}
                        >
                            {getErpId(order)}
                        </Link>
                    </td>
                    <td
                        style={{ whiteSpace: 'nowrap' }}
                        data-sort={new Date(order.date_created).toISOString().split('T')[0].replace(/-/g, '')}
                    >
                        {formatDate(order.date_created)}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatOrderTotal(order)}</td>
                </>
            )}
        />
    );
};

export default MyAccountOrdersPanel;
